import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PerformanceLaunchMonitorService {
    private bootstrapStartTime: number;
    private firstInteractionTime: number;
    private responseDetected: boolean;
    private isMonitoring: boolean;
    private stuckDetected$ = new BehaviorSubject<boolean>(false);
    private lastTs = 0;
    private boundFirstInteraction: () => void;

    // Thresholds in milliseconds
    private readonly BOOTSTRAP_THRESHOLD = 10000; // 10 seconds
    private readonly INTERACTION_RESPONSE_THRESHOLD = 3000; // 3 seconds

    constructor() {
        this.bootstrapStartTime = 0;
        this.firstInteractionTime = 0;
        this.responseDetected = false;
        this.isMonitoring = false;
        this.boundFirstInteraction = this.handleFirstInteraction.bind(this);
        this.initializeMonitoring();
    }

    /**
     * Safe wrapper for performance.now() with fallback for incompatible browsers
     * Returns timestamp in milliseconds
     */
    private safePerformanceNow(): number {
        const perf = globalThis?.performance;
        // Prefer a monotonic, epoch-aligned timestamp if possible
        let now = (perf && typeof perf.now === 'function')
            ? (typeof perf.timeOrigin === 'number' ? perf.timeOrigin + perf.now() : perf.now())
            : Date.now();

        // Ensure monotonic progression even if the clock moves backwards
        if (now <= this.lastTs) now = this.lastTs + 1;
        this.lastTs = now;
        return now;
    }

    /**
     * Initialize performance monitoring for app launch
     */
    private initializeMonitoring(): void {
        // Check for performance API availability (log warning if unavailable but continue with fallback)
        const perf = globalThis?.performance;

        if (!perf || typeof perf.now !== 'function') {
            console.warn('Performance API not fully supported. Using Date.now() fallback for performance monitoring.');
        }

        // Record bootstrap start time
        this.bootstrapStartTime = this.safePerformanceNow();

        // Monitor for first interaction
        if (typeof window !== 'undefined') {
            window.addEventListener('click', this.boundFirstInteraction, { once: false, capture: true });
            window.addEventListener('touchstart', this.boundFirstInteraction, { once: false, capture: true });

            // Monitor long tasks if supported
            this.monitorLongTasks();

            // Set flag to indicate monitoring is active
            this.isMonitoring = true;
        }
    }

    /**
     * Handle first user interaction after apparent loading
     */
    private handleFirstInteraction(): void {
        if (!this.firstInteractionTime && this.isAppLoaded()) {
            this.firstInteractionTime = this.safePerformanceNow();

            // Start checking for response
            setTimeout(() => {
                if (!this.responseDetected) {
                    // No response detected within threshold, app might be stuck
                    if (this.isAppLoaded() && this.isMonitoring) {
                        this.stuckDetected$.next(true);
                    }
                }
            }, this.INTERACTION_RESPONSE_THRESHOLD);
        }
    }

    /**
     * Mark that app has responded to user interaction
     */
    public markResponseDetected(): void {
        this.responseDetected = true;
    }

    /**
     * Mark that bootstrap has completed
     */
    public markBootstrapComplete(): void {
        const bootstrapTime = this.safePerformanceNow() - this.bootstrapStartTime;
        console.warn(`App bootstrap completed in ${bootstrapTime}ms`);
        this.stopMonitoring()
    }

    /**
     * Check if app has been loading too long
     */
    public isBootstrapStuck(): boolean {
        return this.isMonitoring && this.safePerformanceNow() - this.bootstrapStartTime > this.BOOTSTRAP_THRESHOLD;
    }

    /**
     * Monitor for long tasks using Performance Observer if supported
     */
    private monitorLongTasks(): void {
        if (window.PerformanceObserver && PerformanceObserver.supportedEntryTypes &&
            PerformanceObserver.supportedEntryTypes.includes('longtask')) {

            const observer = new PerformanceObserver((list) => {
                list.getEntries().forEach((entry) => {
                    // Consider consecutive long tasks during bootstrap as potential stuck indicator
                    if (this.isMonitoring && !this.responseDetected &&
                        this.safePerformanceNow() - this.bootstrapStartTime < this.BOOTSTRAP_THRESHOLD) {
                        console.warn('Long task detected during app bootstrap:', entry.duration);
                    }
                });
            });

            observer.observe({ entryTypes: ['longtask'] });
        }
    }

    /**
     * Check if basic app loading is complete (estimated)
     */
    private isAppLoaded(): boolean {
        // Basic check to see if DOM appears to be loaded
        return document.readyState === 'complete';
    }

  /**
   * Stop performance monitoring
   */
  public stopMonitoring(): void {
    this.isMonitoring = false;
    if (typeof window !== "undefined" && this.boundFirstInteraction) {
      window.removeEventListener("click", this.boundFirstInteraction, { capture: true });
      window.removeEventListener("touchstart", this.boundFirstInteraction, { capture: true });
    }
  }

    /**
     * Get observable for stuck detection
     */
    public getStuckDetected(): Observable<boolean> {
        return this.stuckDetected$.asObservable();
    }
}